import { useEffect, useState, useRef, PointerEvent } from "react";
import { Button } from "barte-design-system";
import { X } from "@phosphor-icons/react";

import styles from "./modalize.module.css";
import type { ModalizeProps } from "./modalize.type";
import { MODALIZE_ANIMATION_DURATION } from "./modalize.constants";

export const Modalize = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    className = "",
    showCloseButton = true,
}: ModalizeProps) => {
    const [render, setRender] = useState(isOpen);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);

    useEffect(() => {
        if (isOpen) {
            setRender(true);
            document.body.style.overflow = "hidden";
        } else {
            const timer = setTimeout(() => {
                setRender(false);
                document.body.style.overflow = "auto";
                setDragOffsetY(0);
            }, MODALIZE_ANIMATION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
        setIsDragging(true);
        startYRef.current = e.clientY;
        setDragOffsetY(0);
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        const delta = e.clientY - startYRef.current;
        if (delta > 0) {
            setDragOffsetY(delta);
        }
    };

    const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        setIsDragging(false);
        e.currentTarget.releasePointerCapture(e.pointerId);

        const threshold = (containerRef.current?.offsetHeight || 0) * 0.2;
        if (dragOffsetY > threshold) {
            onClose();
        } else {
            setDragOffsetY(0);
        }
    };

    if (!render && !isOpen) return null;

    const footerLayoutClass = footer?.buttonLayout === "full" ? styles["footer-full"] : styles["footer-hug"];

    const containerStyle = {
        transform: isDragging
            ? `translateY(${dragOffsetY}px)`
            : (isOpen ? "translateY(0)" : "translateY(100%)"),
        transition: isDragging ? "none" : undefined,
    };

    return (
        <div
            className={`${styles.backdrop} ${isOpen ? styles.open : ""} ${className}`}
            onClick={onClose}
        >
            <div
                ref={containerRef}
                className={styles.container}
                style={containerStyle}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className={styles["drag-handle-wrapper"]}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    style={{ touchAction: "none" }}
                >
                    <div className={styles["drag-handle"]} />
                </div>

                <div className={styles.header}>
                    <h3 className={styles.title}>{title}</h3>
                    {showCloseButton && (
                        <button className={styles["close-button"]} onClick={onClose}>
                            <X size={20} weight="bold" />
                        </button>
                    )}
                </div>

                <div className={styles.body}>
                    {children}
                </div>

                {footer && (
                    <div className={`${styles.footer} ${footerLayoutClass}`}>
                        {footer.secondaryButton && (
                            <Button
                                variant={footer.secondaryButton.variant || "outline"}
                                onClick={footer.secondaryButton.onClick}
                                disabled={footer.secondaryButton.disabled}
                                loading={footer.secondaryButton.loading}
                                type={footer.secondaryButton.type || "button"}
                            >
                                {footer.secondaryButton.children}
                            </Button>
                        )}
                        {footer.primaryButton && (
                            <Button
                                variant={footer.primaryButton.variant || "fill"}
                                onClick={footer.primaryButton.onClick}
                                disabled={footer.primaryButton.disabled}
                                loading={footer.primaryButton.loading}
                                type={footer.primaryButton.type || "button"}
                            >
                                {footer.primaryButton.children}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
